import {useCallback, useMemo} from 'react';
import {IProduct} from 'modules/product-card/models/ProductResponse';
import {EventNames} from 'modules/google-analytics/constants';
import {sendGtagEvent} from 'hooks/analytics/gtag/helpers';

const YASPLIT_EVENT_CATEGORY = 'yasplit';

const YASPLIT_EVENT_ACTIONS = {
    SHOW_WIDGET: 'show_element_pdp',
    CLICK_WIDGET: 'click_element_pdp',
    SHOW_POPUP: 'show_popup_pdp',
    CLOSE_POPUP: 'close_popup_pdp',
    CLICK_POPUP: 'click_popup_pdp',
} as const;

const buildYaSplitEventLabel = (partPrice?: number | null, parts?: number | null): string | null => {
    if (!partPrice || !parts) {
        return null;
    }

    return `${partPrice}_${parts}`;
};

interface IParams {
    yandexSplitInformer: IProduct['yandexSplitInformer'];
}

interface IResult {
    eventLabel: string | null;
    handleWidgetInView: (inView: boolean) => void;
    handleWidgetClick: () => void;
    handleModalShow: () => void;
    handleModalClose: () => void;
    handlePopupLearnMoreClick: () => void;
}

export const useYandexSplitWidgetGtag = ({yandexSplitInformer}: IParams): IResult => {
    const eventLabel = useMemo(() => {
        return buildYaSplitEventLabel(yandexSplitInformer?.partPrice, yandexSplitInformer?.parts);
    }, [yandexSplitInformer]);

    const handleWidgetInView = useCallback(
        (inView: boolean) => {
            if (!inView || !eventLabel) {
                return;
            }

            sendGtagEvent({
                event: EventNames.OWOX,
                eventCategory: YASPLIT_EVENT_CATEGORY,
                eventAction: YASPLIT_EVENT_ACTIONS.SHOW_WIDGET,
                eventLabel,
            });
        },
        [eventLabel],
    );

    const handleWidgetClick = useCallback(() => {
        if (!eventLabel) {
            return;
        }

        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: YASPLIT_EVENT_CATEGORY,
            eventAction: YASPLIT_EVENT_ACTIONS.CLICK_WIDGET,
            eventLabel,
        });
    }, [eventLabel]);

    const handleModalShow = useCallback(() => {
        if (!eventLabel) {
            return;
        }

        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: YASPLIT_EVENT_CATEGORY,
            eventAction: YASPLIT_EVENT_ACTIONS.SHOW_POPUP,
            eventLabel,
        });
    }, [eventLabel]);

    const handleModalClose = useCallback(() => {
        if (!eventLabel) {
            return;
        }

        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: YASPLIT_EVENT_CATEGORY,
            eventAction: YASPLIT_EVENT_ACTIONS.CLOSE_POPUP,
            eventLabel,
        });
    }, [eventLabel]);

    const handlePopupLearnMoreClick = useCallback(() => {
        if (!eventLabel) {
            return;
        }

        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: YASPLIT_EVENT_CATEGORY,
            eventAction: YASPLIT_EVENT_ACTIONS.CLICK_POPUP,
            eventLabel,
        });
    }, [eventLabel]);

    return {
        eventLabel,
        handleWidgetInView,
        handleWidgetClick,
        handleModalShow,
        handleModalClose,
        handlePopupLearnMoreClick,
    };
};
